/* ============================================================
   Lagomlo — design-studio.js (Shopify theme)
   The Design Studio wizard (DESIGN.md §6): dependency-free,
   vanilla JS that works in the Liquid asset context.

   Steps: 1 Product (size/material with live price) →
          2 Upload & crop (client-side, aspect per product) →
          3 Personalize (text/font/color/position/filter/date) →
          4 Review (canvas composite + price summary + Add to cart)

   Add to cart submits the surrounding Shopify product form with:
     - variant id + quantity
     - properties[Design]         → JSON design object (DESIGN.md §6)
     - properties[Design Summary] → readable text for cart/orders
     - properties[Design Image]   → native file-upload property,
       populated via DataTransfer when supported; otherwise the
       cropped image is embedded in the Design JSON (data URL).

   Exposed as window.LagomloStudio.
   ============================================================ */
(function (global) {
  'use strict';

  var FREE_SHIPPING_THRESHOLD = 6900; // $69.00 in cents
  var FLAT_SHIPPING = 699;            // $6.99 in cents
  var TAX_RATE = 0.08;

  var FONTS = {
    script: "'Brush Script MT', 'Snell Roundhand', 'Segoe Script', cursive",
    serif: "'Fraunces', Georgia, 'Times New Roman', serif",
    sans: "'DM Sans', system-ui, -apple-system, sans-serif",
    bold: "'Arial Black', 'DM Sans', sans-serif"
  };
  var FILTERS = {
    original: 'none',
    warm: 'sepia(0.25) saturate(1.35) hue-rotate(-8deg)',
    bw: 'grayscale(1) contrast(1.05)',
    vivid: 'saturate(1.6) contrast(1.12)'
  };

  var moneyFmt = new Intl.NumberFormat((global.Lagomlo && global.Lagomlo.money && global.Lagomlo.money.locale) || 'en-US', {
    style: 'currency',
    currency: (global.Lagomlo && global.Lagomlo.money && global.Lagomlo.money.currency) || 'USD'
  });

  function fmt(cents) {
    return moneyFmt.format((cents || 0) / 100);
  }

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // roundRect polyfill (older Safari / Firefox)
  if (typeof CanvasRenderingContext2D !== 'undefined' && !CanvasRenderingContext2D.prototype.roundRect) {
    CanvasRenderingContext2D.prototype.roundRect = function (x, y, w, h, r) {
      if (typeof r === 'number') r = { tl: r, tr: r, br: r, bl: r };
      else r = r || {};
      var tl = r.tl || 0, tr = r.tr || 0, br = r.br || 0, bl = r.bl || 0;
      this.moveTo(x + tl, y);
      this.lineTo(x + w - tr, y);
      this.quadraticCurveTo(x + w, y, x + w, y + tr);
      this.lineTo(x + w, y + h - br);
      this.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
      this.lineTo(x + bl, y + h);
      this.quadraticCurveTo(x, y + h, x, y + h - bl);
      this.lineTo(x, y + tl);
      this.quadraticCurveTo(x, y, x + tl, y);
      this.closePath();
      return this;
    };
  }

  /* ---------- Studio instance ---------- */

  function Studio(root) {
    this.root = root;
    this.form = qs(root, '[data-studio-form]');
    this.step = 1;
    this.state = {
      productId: null,
      productData: null,      // parsed JSON product
      variantId: null,
      variant: null,
      imageDataUrl: null,
      imageFile: null,        // cropped File (for DataTransfer upload)
      crop: { zoom: 1, ox: 0, oy: 0, loaded: false },
      text: { line1: '', line2: '', line3: '', font: 'script', color: '#FFFFFF', position: 'center', date: '' },
      filter: 'original'
    };
    this._dragDepth = 0;
    this._bind();
    this._initProducts();
  }

  Studio.prototype._bind = function () {
    var self = this;

    // Step navigation
    qsa(this.root, '[data-studio-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.goNext(parseInt(btn.getAttribute('data-studio-next'), 10)); });
    });
    qsa(this.root, '[data-studio-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.goBack(parseInt(btn.getAttribute('data-studio-back'), 10)); });
    });

    // Product picker chips (multi-product mode)
    var picker = qs(this.root, '[data-studio-product-picker]');
    if (picker) {
      qsa(this.root, '[data-studio-product-chip]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          qsa(picker, '[data-studio-product-chip]').forEach(function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          self.selectProduct(chip.getAttribute('data-studio-product-chip'));
        });
      });
    }

    // Option selects (built dynamically in _buildOptions)
    // Upload dropzone
    var dropzone = qs(this.root, '[data-studio-dropzone]');
    var fileInput = qs(this.root, '[data-studio-file]');
    if (dropzone && fileInput) {
      dropzone.addEventListener('click', function () { fileInput.click(); });
      dropzone.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
      });
      fileInput.addEventListener('change', function () {
        if (fileInput.files && fileInput.files.length) { self.loadFile(fileInput.files[0]); }
      });
      ['dragenter', 'dragover'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) {
          e.preventDefault();
          self._dragDepth += 1;
          dropzone.classList.add('is-dragging');
        });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        dropzone.addEventListener(ev, function (e) {
          e.preventDefault();
          self._dragDepth = Math.max(0, self._dragDepth - 1);
          if (self._dragDepth === 0) dropzone.classList.remove('is-dragging');
          if (ev === 'drop' && e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
            self.loadFile(e.dataTransfer.files[0]);
          }
        });
      });
    }

    // Crop pan (pointer drag)
    var cropCanvas = qs(this.root, '[data-studio-crop-canvas]');
    if (cropCanvas) {
      var drag = null;
      cropCanvas.addEventListener('pointerdown', function (e) {
        drag = { x: e.clientX, y: e.clientY, ox: self.state.crop.ox, oy: self.state.crop.oy };
        cropCanvas.setPointerCapture(e.pointerId);
      });
      cropCanvas.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var sx = cropCanvas.width / cropCanvas.getBoundingClientRect().width;
        var sy = cropCanvas.height / cropCanvas.getBoundingClientRect().height;
        self.state.crop.ox = drag.ox + (e.clientX - drag.x) * sx;
        self.state.crop.oy = drag.oy + (e.clientY - drag.y) * sy;
        self._drawCrop();
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        cropCanvas.addEventListener(ev, function () { drag = null; });
      });
    }

    var zoom = qs(this.root, '[data-studio-zoom]');
    if (zoom) {
      zoom.addEventListener('input', function () {
        self.state.crop.zoom = parseFloat(zoom.value) || 1;
        self._drawCrop();
      });
    }

    var replace = qs(this.root, '[data-studio-replace]');
    if (replace) {
      replace.addEventListener('click', function () { self.resetUpload(); });
    }

    // Personalization inputs
    qsa(this.root, '[data-studio-text-line]').forEach(function (input) {
      input.addEventListener('input', function () {
        self.state.text[input.getAttribute('data-studio-text-line')] = input.value;
        self._renderLive();
      });
    });
    var fontSel = qs(this.root, '[data-studio-font]');
    if (fontSel) fontSel.addEventListener('change', function () { self.state.text.font = fontSel.value; self._renderLive(); });
    var colorInput = qs(this.root, '[data-studio-text-color]');
    if (colorInput) colorInput.addEventListener('input', function () { self.state.text.color = colorInput.value; self._renderLive(); });
    var posSel = qs(this.root, '[data-studio-position]');
    if (posSel) posSel.addEventListener('change', function () { self.state.text.position = posSel.value; self._renderLive(); });
    var filterSel = qs(this.root, '[data-studio-filter]');
    if (filterSel) filterSel.addEventListener('change', function () { self.state.filter = filterSel.value; self._renderLive(); });
    var dateInput = qs(this.root, '[data-studio-date]');
    if (dateInput) dateInput.addEventListener('change', function () { self.state.text.date = self._formatDate(dateInput.value); self._renderLive(); });

    // Form submit
    var submitBtn = qs(this.root, '[data-studio-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        if (!self.validateReview()) {
          e.preventDefault();
          return;
        }
        self._populateProperties();
      });
    }
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        if (!self.validateReview()) {
          e.preventDefault();
          return;
        }
        self._populateProperties();
        self._submit(e);
      });
    }
  };

  /* ---------- Products & variants ---------- */

  Studio.prototype._initProducts = function () {
    var scripts = qsa(this.root, '[data-studio-product-json]');
    var data = [];
    scripts.forEach(function (s) {
      try {
        var parsed = JSON.parse(s.textContent);
        if (parsed && parsed.variants) data.push(parsed);
      } catch (err) { /* skip malformed payload */ }
    });
    this.products = data;
    if (!this.products.length) {
      this.showError(qs(this.root, '[data-studio-panel="1"]') || this.root, 'No product data found for the Design Studio.');
      return;
    }
    this.selectProduct(String(this.products[0].id));
  };

  Studio.prototype.selectProduct = function (id) {
    var self = this;
    var next = null;
    this.products.forEach(function (p) { if (String(p.id) === String(id)) next = p; });
    if (!next) return;
    this.state.productData = next;
    this.state.productId = next.id;
    this._buildOptions();
    // pick first available variant, else first
    var chosen = null;
    next.variants.forEach(function (v) { if (!chosen && v.available) chosen = v; });
    if (!chosen && next.variants.length) chosen = next.variants[0];
    if (chosen) {
      this.state.variant = chosen;
      this.state.variantId = chosen.id;
      this._syncOptionSelects();
    }
    this._updatePrice();
    this._renderLive();
  };

  Studio.prototype._buildOptions = function () {
    var self = this;
    var container = qs(this.root, '[data-studio-options]');
    var p = this.state.productData;
    if (!container || !p) return;
    container.innerHTML = '';
    p.options.forEach(function (optName, idx) {
      var values = [];
      p.variants.forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      var field = document.createElement('label');
      field.className = 'studio__field';
      var span = document.createElement('span');
      span.className = 'studio__field-label';
      span.textContent = optName;
      var select = document.createElement('select');
      select.setAttribute('data-studio-option', String(idx));
      values.forEach(function (val) {
        var opt = document.createElement('option');
        opt.value = val;
        opt.textContent = val;
        select.appendChild(opt);
      });
      select.addEventListener('change', function () { selfOnChange(idx, select.value); });
      field.appendChild(span);
      field.appendChild(select);
      container.appendChild(field);
    });

    var selfOnChange = function (idx, value) {
      var combo = [];
      qsa(container, '[data-studio-option]').forEach(function (sel) {
        combo.push(sel.value);
      });
      self._matchVariant(combo);
    };
    // store closure refs on this for reuse
    this._optionChange = selfOnChange;
  };

  Studio.prototype._matchVariant = function (combo) {
    var p = this.state.productData;
    var chosen = null;
    p.variants.forEach(function (v) {
      var match = combo.every(function (val, idx) {
        return String(v['option' + (idx + 1)] || '') === String(val || '');
      });
      if (match && !chosen) chosen = v;
    });
    if (chosen) {
      this.state.variant = chosen;
      this.state.variantId = chosen.id;
    } else {
      this.state.variant = null;
      this.state.variantId = null;
    }
    this._updatePrice();
  };

  Studio.prototype._syncOptionSelects = function () {
    var self = this;
    qsa(this.root, '[data-studio-option]').forEach(function (sel, idx) {
      var val = self.state.variant && self.state.variant['option' + (idx + 1)];
      if (val) sel.value = val;
    });
  };

  Studio.prototype._updatePrice = function () {
    var priceEl = qs(this.root, '[data-studio-price]');
    var unavail = qs(this.root, '[data-studio-unavailable]');
    var vidInput = qs(this.root, '[data-studio-variant-id]');
    if (this.state.variant) {
      if (priceEl) priceEl.textContent = fmt(this.state.variant.price);
      if (unavail) unavail.hidden = true;
      if (vidInput) vidInput.value = this.state.variantId;
      // product chips price refresh
      qsa(this.root, '[data-studio-product-chip-price]').forEach(function () { /* no-op */ });
    } else {
      if (priceEl) priceEl.textContent = '—';
      if (unavail) unavail.hidden = false;
      if (vidInput) vidInput.value = '';
    }
  };

  /* ---------- Step navigation ---------- */

  Studio.prototype.setStep = function (n) {
    this.step = n;
    var self = this;
    qsa(this.root, '[data-step-panel]').forEach(function (panel) {
      var num = parseInt(panel.getAttribute('data-step-panel'), 10);
      panel.hidden = num !== n;
    });
    qsa(this.root, '[data-step-indicator]').forEach(function (ind) {
      var num = parseInt(ind.getAttribute('data-step-indicator'), 10);
      ind.classList.toggle('is-active', num === n);
      ind.classList.toggle('is-done', num < n);
    });
    if (n === 4) this._renderReview();
    if (n === 3) this._renderLive();
    if (n === 2) this._drawCrop();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  Studio.prototype.goNext = function (fromStep) {
    if (fromStep !== this.step) return;
    if (!this.validateStep(fromStep)) return;
    this.setStep(fromStep + 1);
  };

  Studio.prototype.goBack = function (fromStep) {
    if (fromStep !== this.step) return;
    this.setStep(fromStep - 1);
  };

  Studio.prototype.showError = function (target, message) {
    var err = qs(this.root, '[data-studio-error]');
    if (err) {
      err.textContent = message;
      err.hidden = false;
    } else if (target) {
      var p = document.createElement('p');
      p.className = 'studio__error';
      p.textContent = message;
      target.appendChild(p);
    }
  };

  Studio.prototype.clearError = function () {
    var err = qs(this.root, '[data-studio-error]');
    if (err) err.hidden = true;
  };

  Studio.prototype.validateStep = function (step) {
    this.clearError();
    if (step === 1) {
      if (!this.state.variant) {
        this.showError(this.root, 'Please choose an available size and material.');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (this.requiresPhoto() && !this.state.imageDataUrl) {
        this.showError(this.root, 'Please upload a photo first.');
        return false;
      }
      return true;
    }
    return true;
  };

  Studio.prototype.validateReview = function () {
    this.clearError();
    if (!this.state.variant) {
      this.showError(this.root, 'Please choose an available size and material.');
      return false;
    }
    if (this.requiresPhoto() && !this.state.imageDataUrl) {
      this.showError(this.root, 'Please upload a photo first.');
      return false;
    }
    return true;
  };

  Studio.prototype.requiresPhoto = function () {
    var handle = this.state.productData ? (this.state.productData.handle || '') : '';
    // Text blankets are the only MVP product that doesn't need a photo
    return handle.indexOf('text-blanket') === -1;
  };

  Studio.prototype.aspectRatio = function () {
    var handle = this.state.productData ? (this.state.productData.handle || '') : '';
    if (handle.indexOf('pillow') !== -1 || handle.indexOf('mug') !== -1) return 1; // 1:1
    return 4 / 3; // blankets
  };

  /* ---------- Upload & crop ---------- */

  Studio.prototype.loadFile = function (file) {
    var self = this;
    if (!file || file.type.indexOf('image/') !== 0) {
      this.showError(this.root, 'Please choose a JPG or PNG image.');
      return;
    }
    var reader = new FileReader();
    reader.onload = function () {
      self.loadImageDataURL(reader.result);
    };
    reader.readAsDataURL(file);
  };

  /* Load an image from a data URL (used by AI mode results) */
  Studio.prototype.loadImageDataURL = function (dataUrl) {
    var self = this;
    if (!dataUrl) return;
    var img = new Image();
    img.onload = function () {
      self._img = img;
      self.state.crop = { zoom: 1, ox: 0, oy: 0, loaded: true };
      self.state.imageDataUrl = dataUrl;
      var drop = qs(self.root, '[data-studio-dropzone]');
      var crop = qs(self.root, '[data-studio-crop]');
      if (drop) drop.hidden = true;
      if (crop) crop.hidden = false;
      self._drawCrop();
    };
    img.onerror = function () {
      self.showError(self.root, 'That image could not be read. Try another one.');
    };
    img.src = dataUrl;
  };

  Studio.prototype.resetUpload = function () {
    this.state.imageDataUrl = null;
    this.state.imageFile = null;
    this.state.crop = { zoom: 1, ox: 0, oy: 0, loaded: false };
    var fileInput = qs(this.root, '[data-studio-file]');
    if (fileInput) fileInput.value = '';
    var dz = qs(this.root, '[data-studio-dropzone]');
    var crop = qs(this.root, '[data-studio-crop]');
    if (dz) dz.hidden = false;
    if (crop) crop.hidden = true;
  };

  Studio.prototype.cropCanvasSize = function () {
    var ar = this.aspectRatio();
    if (ar >= 1) return { w: 900, h: 900 };
    return { w: 900, h: Math.round(900 / ar) };
  };

  Studio.prototype._drawCrop = function () {
    var canvas = qs(this.root, '[data-studio-crop-canvas]');
    if (!canvas || !this.state.crop.loaded || !this._img) return;
    var size = this.cropCanvasSize();
    canvas.width = size.w;
    canvas.height = size.h;
    var ctx = canvas.getContext('2d');
    var c = this.state.crop;

    // cover-fit base scale
    var scale = Math.max(size.w / this._img.naturalWidth, size.h / this._img.naturalHeight) * c.zoom;
    var dw = this._img.naturalWidth * scale;
    var dh = this._img.naturalHeight * scale;
    var dx = (size.w - dw) / 2 + c.ox;
    var dy = (size.h - dh) / 2 + c.oy;

    // clamp so the image always covers the canvas
    dx = Math.min(0, Math.max(size.w - dw, dx));
    dy = Math.min(0, Math.max(size.h - dh, dy));

    ctx.fillStyle = '#1c1512';
    ctx.fillRect(0, 0, size.w, size.h);
    ctx.drawImage(this._img, dx, dy, dw, dh);

    // rule-of-thirds guides
    ctx.strokeStyle = 'rgba(255,255,255,.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 5]);
    ctx.beginPath();
    ctx.moveTo(size.w / 3, 0); ctx.lineTo(size.w / 3, size.h);
    ctx.moveTo((size.w / 3) * 2, 0); ctx.lineTo((size.w / 3) * 2, size.h);
    ctx.moveTo(0, size.h / 3); ctx.lineTo(size.w, size.h / 3);
    ctx.moveTo(0, (size.h / 3) * 2); ctx.lineTo(size.w, (size.h / 3) * 2);
    ctx.stroke();
  };

  Studio.prototype._applyCrop = function () {
    var canvas = qs(this.root, '[data-studio-crop-canvas]');
    if (!canvas) return;
    var self = this;
    this.state.imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    // Build a File for the native line-item upload
    if (canvas.toBlob) {
      canvas.toBlob(function (blob) {
        if (!blob) return;
        var name = 'design-' + Date.now() + '.jpg';
        self.state.imageFile = new File([blob], name, { type: 'image/jpeg' });
        self._assignFileInput();
      }, 'image/jpeg', 0.8);
    } else {
      this.state.imageFile = null;
    }
  };

  Studio.prototype._assignFileInput = function () {
    var input = qs(this.root, '[data-studio-file]');
    if (!input || !this.state.imageFile) return;
    try {
      if (window.DataTransfer) {
        var dt = new DataTransfer();
        dt.items.add(this.state.imageFile);
        input.files = dt.files;
      }
    } catch (e) { /* fall back to imageDataUrl inside Design JSON */ }
  };

  /* ---------- Personalize & composite ---------- */

  Studio.prototype.textLines = function () {
    var t = this.state.text;
    var lines = [t.line1, t.line2, t.line3].filter(function (l) { return l && l.trim(); });
    if (t.date) lines.push(t.date);
    return lines;
  };

  Studio.prototype._renderLive = function () {
    var canvas = qs(this.root, '[data-studio-live-canvas]');
    if (!canvas) return;
    this._composite(canvas, 320);
  };

  Studio.prototype._renderReview = function () {
    var canvas = qs(this.root, '[data-studio-review-canvas]');
    if (!canvas) return;
    this._composite(canvas, 560);
    this._renderSummary();
  };

  Studio.prototype._composite = function (canvas, baseSize) {
    var self = this;
    var ctx = canvas.getContext('2d');
    var ar = this.aspectRatio();
    var h = Math.round(baseSize * 1.25);
    var w = ar >= 1 ? h : Math.round(h * ar);
    // keep within a reasonable vertical ratio for the silhouette
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);

    // soft page background
    ctx.fillStyle = '#F7EFE4';
    ctx.fillRect(0, 0, w, h);

    var s = this.state;
    var photo = null;
    if (s.imageDataUrl) {
      photo = new Image();
      photo.onload = function () { selfDraw(); };
      photo.src = s.imageDataUrl;
    }
    var selfDraw = function () { doDraw(photo); };
    if (!photo) doDraw(null);

    function doDraw(img) {
      // silhouettes share the drawing area
      var m = w * 0.08;
      var bw = w - m * 2;
      var bh = h - m * 2.2;

      if (ar >= 1) {
        // pillow / mug: square-ish product
        var isMug = (self.state.productData && self.state.productData.handle || '').indexOf('mug') !== -1;
        drawSquareProduct(ctx, w, h, m, img, isMug);
      } else {
        drawBlanket(ctx, w, h, m, bw, bh, img);
      }

      // text
      var lines = self.textLines();
      if (lines.length) {
        drawText(ctx, w, h, lines, self.state.text);
      }

      // subtle vignette
      var grad = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.75);
      grad.addColorStop(0, 'rgba(62,47,38,0)');
      grad.addColorStop(1, 'rgba(62,47,38,0.10)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }

    function drawBlanket(ctx, w, h, m, bw, bh, img) {
      // draped blanket silhouette
      var top = m * 0.7;
      var r = bw * 0.06;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(m + r, top);
      ctx.quadraticCurveTo(m, top, m, top + r);
      ctx.lineTo(m, top + bh * 0.96);
      // wavy bottom edge (3 scallops)
      var waveW = bw / 3;
      for (var i = 0; i < 3; i++) {
        var wx = m + i * waveW;
        ctx.quadraticCurveTo(wx + waveW / 2, top + bh + bh * 0.10, wx + waveW, top + bh * 0.96);
      }
      ctx.lineTo(m + bw, top + r);
      ctx.quadraticCurveTo(m + bw, top, m + bw - r, top);
      ctx.closePath();

      // photo clipped into blanket
      ctx.save();
      ctx.clip();
      if (img) {
        if (ctx.filter !== undefined) ctx.filter = FILTERS[s.filter] || 'none';
        var cover = Math.max(bw / img.naturalWidth, bh / img.naturalHeight) * 1.06;
        var dw = img.naturalWidth * cover;
        var dh = img.naturalHeight * cover;
        ctx.drawImage(img, m + (bw - dw) / 2, top + (bh - dh) / 2, dw, dh);
        if (ctx.filter !== undefined) ctx.filter = 'none';
        // soft warm wash
        ctx.fillStyle = 'rgba(255,236,220,0.08)';
        ctx.fillRect(m, top, bw, bh);
      } else {
        var grad = ctx.createLinearGradient(m, top, m + bw, top + bh);
        grad.addColorStop(0, '#F2E9DD');
        grad.addColorStop(1, '#E9C9BC');
        ctx.fillStyle = grad;
        ctx.fillRect(m, top, bw, bh);
        ctx.fillStyle = 'rgba(196,112,79,0.35)';
        ctx.font = 'italic ' + (bw * 0.08) + "px Georgia, serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Your photo here', w / 2, top + bh / 2);
      }
      ctx.restore();

      // top fold band
      ctx.beginPath();
      ctx.moveTo(m, top + bh * 0.12);
      ctx.lineTo(m + bw, top + bh * 0.12);
      ctx.strokeStyle = 'rgba(62,47,38,0.18)';
      ctx.lineWidth = Math.max(2, w * 0.006);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(m + 4, top + bh * 0.14);
      ctx.lineTo(m + bw - 4, top + bh * 0.14);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = Math.max(1.5, w * 0.004);
      ctx.stroke();

      // stitched border
      ctx.setLineDash([6, 6]);
      ctx.strokeStyle = 'rgba(62,47,38,0.35)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(m + r, top + r * 0.5);
      ctx.quadraticCurveTo(m + r * 0.5, top + r * 0.5, m + r * 0.5, top + r);
      ctx.lineTo(m + r * 0.5, top + bh * 0.96 - 4);
      ctx.lineTo(m + bw - r * 0.5, top + bh * 0.96 - 4);
      ctx.lineTo(m + bw - r * 0.5, top + r);
      ctx.quadraticCurveTo(m + bw - r * 0.5, top + r * 0.5, m + bw - r, top + r * 0.5);
      ctx.stroke();
      ctx.restore();
    }

    function drawSquareProduct(ctx, w, h, m, img, isMug) {
      var side = Math.min(w, h) * 0.72;
      var x = (w - side) / 2;
      var y = (h - side) / 2 - h * 0.02;
      var r = side * 0.14;
      ctx.save();
      if (isMug) {
        // mug handle
        ctx.strokeStyle = 'rgba(62,47,38,0.55)';
        ctx.lineWidth = Math.max(4, w * 0.02);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.arc(x + side + w * 0.01, y + side * 0.42, side * 0.16, -Math.PI / 2.2, Math.PI / 2.2);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.roundRect(x, y, side, side, r);
      // photo
      ctx.save();
      ctx.clip();
      if (img) {
        if (ctx.filter !== undefined) ctx.filter = FILTERS[s.filter] || 'none';
        var cover = Math.max(side / img.naturalWidth, side / img.naturalHeight) * 1.06;
        ctx.drawImage(img, x + (side - img.naturalWidth * cover) / 2, y + (side - img.naturalHeight * cover) / 2, img.naturalWidth * cover, img.naturalHeight * cover);
        if (ctx.filter !== undefined) ctx.filter = 'none';
      } else {
        ctx.fillStyle = '#F2E9DD';
        ctx.fillRect(x, y, side, side);
        ctx.fillStyle = 'rgba(196,112,79,0.35)';
        ctx.font = 'italic ' + (side * 0.08) + "px Georgia, serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Your photo here', w / 2, y + side / 2);
      }
      ctx.restore();
      // seam
      ctx.setLineDash([7, 6]);
      ctx.strokeStyle = 'rgba(62,47,38,0.35)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x + side * 0.06, y + side * 0.06, side * 0.88, side * 0.88, r * 0.7);
      ctx.stroke();
      // corner buttons
      var t = side * 0.07;
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(196,112,79,0.55)';
      [[x + t, y + t], [x + side - t, y + t], [x + t, y + side - t], [x + side - t, y + side - t]].forEach(function (p) {
        ctx.beginPath();
        ctx.arc(p[0], p[1], Math.max(2, w * 0.008), 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
    }
  };

  function drawText(ctx, w, h, lines, textState) {
    var fontFamily = FONTS[textState.font] || FONTS.serif;
    var weight = textState.font === 'bold' ? '800' : '400';
    var baseSize = Math.max(16, w * (lines.length > 2 ? 0.075 : 0.09));
    if (lines.length > 3) baseSize = Math.max(14, w * 0.06);
    var lineHeight = baseSize * 1.25;
    var totalH = lines.length * lineHeight;

    var startY;
    if (textState.position === 'top') startY = h * 0.16;
    else if (textState.position === 'bottom') startY = h * 0.82 - totalH;
    else startY = (h - totalH) / 2;

    ctx.save();
    ctx.font = weight + ' ' + baseSize + 'px ' + fontFamily;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // shadow for legibility
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = Math.max(4, w * 0.012);
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = textState.color || '#FFFFFF';
    lines.forEach(function (line, i) {
      ctx.fillText(line, w / 2, startY + i * lineHeight + lineHeight / 2);
    });
    ctx.restore();
  }

  /* ---------- Price summary & add to cart ---------- */

  Studio.prototype._renderSummary = function () {
    var set = function (sel, val) {
      var el = qs(this.root, sel);
      if (el) el.textContent = val;
    }.bind(this);

    var v = this.state.variant;
    var p = this.state.productData;
    if (!v) return;

    var subtotal = v.price; // qty fixed at 1 in the studio
    var shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING;
    var tax = Math.round(subtotal * TAX_RATE);
    var total = subtotal + shipping + tax;

    set('[data-studio-summary-size]', v.option1 || '—');
    set('[data-studio-summary-material]', v.option2 || v.option3 || '—');
    var lines = this.textLines();
    set('[data-studio-summary-text]', lines.length ? lines.join(' · ') : 'None');
    var filterLabel = this.state.filter === 'original' ? 'Original' : this.state.filter.charAt(0).toUpperCase() + this.state.filter.slice(1);
    set('[data-studio-summary-filter]', filterLabel);
    set('[data-studio-summary-subtotal]', fmt(subtotal));
    set('[data-studio-summary-shipping]', shipping === 0 ? 'Free' : fmt(shipping));
    set('[data-studio-summary-tax]', fmt(tax));
    set('[data-studio-summary-total]', fmt(total));
  };

  Studio.prototype._designObject = function () {
    var v = this.state.variant;
    var p = this.state.productData;
    return {
      product: p.handle,
      size: v.option1 || '',
      material: v.option2 || v.option3 || '',
      imageDataUrl: this.state.imageDataUrl || '',
      text: {
        line1: this.state.text.line1 || '',
        line2: this.state.text.line2 || '',
        line3: this.state.text.line3 || '',
        font: this.state.text.font,
        color: this.state.text.color,
        position: this.state.text.position,
        date: this.state.text.date || ''
      },
      filters: this.state.filter
    };
  };

  Studio.prototype._summaryString = function () {
    var v = this.state.variant;
    var bits = [];
    if (v && v.option1) bits.push(v.option1);
    if (v && v.option2) bits.push(v.option2);
    var lines = this.textLines();
    if (lines.length) {
      bits.push(lines.join(' · '));
    }
    if (this.state.filter !== 'original') {
      bits.push(this.state.filter.charAt(0).toUpperCase() + this.state.filter.slice(1) + ' filter');
    }
    return bits.join(' — ');
  };

  Studio.prototype._populateProperties = function () {
    var design = qs(this.root, '[data-studio-property-design]');
    var summary = qs(this.root, '[data-studio-property-summary]');
    if (design) design.value = JSON.stringify(this._designObject());
    if (summary) summary.value = this._summaryString();
  };

  Studio.prototype._submit = function (e) {
    var self = this;
    var submitBtn = qs(this.root, '[data-studio-submit]');
    var routes = (global.Lagomlo && global.Lagomlo.routes) || {};

    // Prefer the AJAX cart API so the drawer opens without a page reload.
    if (this.form && routes.cartAdd && window.fetch && this.form.method !== 'get') {
      e.preventDefault();
      if (submitBtn) submitBtn.disabled = true;
      var fd = new FormData(this.form);
      fetch(routes.cartAdd, { method: 'POST', body: fd })
        .then(function (res) {
          if (!res.ok) throw new Error('bad status ' + res.status);
          return res.json();
        })
        .then(function () {
          if (submitBtn) submitBtn.disabled = false;
          self._afterAdd();
        })
        .catch(function () {
          if (submitBtn) submitBtn.disabled = false;
          // Fall back to the native form POST (adds via /cart/add, then Shopify redirects to cart)
          if (self.form) {
            var native = document.createElement('form');
            native.method = self.form.method || 'post';
            native.action = self.form.action;
            native.innerHTML = self.form.innerHTML;
            document.body.appendChild(native);
            native.submit();
          }
        });
    }
  };

  Studio.prototype._afterAdd = function () {
    // Open / refresh the cart drawer if theme.js is present, else navigate to cart.
    if (global.LagomloCart && typeof global.LagomloCart.afterAdd === 'function') {
      global.LagomloCart.afterAdd();
    } else if (global.Lagomlo && global.Lagomlo.routes && global.Lagomlo.routes.cart) {
      window.location.href = global.Lagomlo.routes.cart;
    }
  };

  Studio.prototype._formatDate = function (val) {
    if (!val) return '';
    var parts = val.split('-');
    if (parts.length !== 3) return val;
    return parts[1] + '/' + parts[2] + '/' + parts[0];
  };

  /* ============================================================
     Category + AI fusion layer (2026-08-25)
     Reads window.LagomloCategories (category-config.js), drives:
       - category select bar
       - dynamic size/material/process options with live price
       - production days + fast-delivery hint ("order today, arrive by X")
       - photo/AI dual mode in step 2
     Writes chosen options into the line-item Design property so the
     order records product type / size / material / process.
     ============================================================ */

  var CATS = (global.LagomloCategories) || {};

  function priceMoney(amount) {
    var cur = (global.Lagomlo && global.Lagomlo.money && global.Lagomlo.money.currency) || 'USD';
    return cur + ' ' + Number(amount).toFixed(2);
  }

  function calcDeliveryHint(cat) {
    // production days (string like "5-7 business days") -> range in days
    var m = cat.productionDays && cat.productionDays.match(/(\d+)\s*-\s*(\d+)/);
    var min = m ? parseInt(m[1], 10) : 6;
    var max = m ? parseInt(m[2], 10) : 9;
    var now = new Date();
    var dMin = new Date(now); dMin.setDate(dMin.getDate() + min);
    var dMax = new Date(now); dMax.setDate(dMax.getDate() + max);
    var fmt = function (d) { return (d.getMonth() + 1) + '/' + d.getDate(); };
    return 'Order today — arrives ' + fmt(dMin) + ' – ' + fmt(dMax);
  }

  function initCategoryFusion() {
    var roots = qsa(document, '[data-design-studio]');
    roots.forEach(function (root) {
      var keys = Object.keys(CATS);
      if (!keys.length) return;

      var bar = qs(root, '[data-studio-category-bar]');
      var select = qs(root, '[data-studio-category-select]');
      var hint = qs(root, '[data-studio-category-hint]');
      var delivery = qs(root, '[data-studio-delivery]');
      var deliveryText = qs(root, '[data-studio-delivery-text]');
      if (!select) return;

      // populate select
      keys.forEach(function (k) {
        var opt = document.createElement('option');
        opt.value = k;
        opt.textContent = CATS[k].name;
        select.appendChild(opt);
      });
      bar.hidden = false;

      var studio = root.__studio;
      function applyCategory() {
        var k = select.value;
        var cat = CATS[k];
        if (!cat) return;
        // hint
        if (hint) {
          hint.textContent = cat.productionDays;
          hint.style.display = 'block';
        }
        // delivery hint
        if (delivery && deliveryText) {
          deliveryText.textContent = calcDeliveryHint(cat);
          delivery.hidden = false;
        }
        // build option selects in step 1 (size/material/process)
        var optBox = qs(root, '[data-studio-options]');
        if (optBox) {
          optBox.innerHTML = '';
          ['sizes', 'materials', 'processes'].forEach(function (group, gi) {
            var list = cat[group] || [];
            if (!list.length) return;
            var field = document.createElement('label');
            field.className = 'studio__field';
            var span = document.createElement('span');
            span.className = 'studio__field-label';
            span.textContent = group.charAt(0).toUpperCase() + group.slice(1);
            var sel = document.createElement('select');
            sel.setAttribute('data-studio-cat-opt', group);
            list.forEach(function (item, ii) {
              var o = document.createElement('option');
              o.value = String(ii);
              o.textContent = item.label + (item.popular ? ' (Popular)' : '');
              sel.appendChild(o);
            });
            sel.addEventListener('change', updatePriceAndSummary);
            field.appendChild(span); field.appendChild(sel);
            optBox.appendChild(field);
          });
        }
        // price + summary sync
        updatePriceAndSummary();
      }

      function currentCatSelection() {
        var k = select.value; var cat = CATS[k]; if (!cat) return null;
        var out = { category: k, categoryName: cat.name, size: null, material: null, process: null };
        ['sizes', 'materials', 'processes'].forEach(function (group) {
          var sel = qs(root, '[data-studio-cat-opt="' + group + '"]');
          if (sel && cat[group] && cat[group][sel.value]) out[group.slice(0, -1)] = cat[group][sel.value];
        });
        return out;
      }

      function updatePriceAndSummary() {
        var cat = CATS[select.value]; if (!cat) return;
        var selSize = qs(root, '[data-studio-cat-opt="sizes"]');
        var selMat = qs(root, '[data-studio-cat-opt="materials"]');
        var selProc = qs(root, '[data-studio-cat-opt="processes"]');
        var price = 0, compareAt = 0;
        if (selSize && cat.sizes[selSize.value]) {
          price += Number(cat.sizes[selSize.value].price) || 0;
          compareAt = Number(cat.sizes[selSize.value].compareAt) || 0;
        }
        if (selMat && cat.materials[selMat.value]) price += Number(cat.materials[selMat.value].surcharge) || 0;
        if (selProc && cat.processes[selProc.value]) price += Number(cat.processes[selProc.value].surcharge) || 0;
        var priceEl = qs(root, '[data-studio-price]');
        if (priceEl) {
          priceEl.textContent = compareAt > price
            ? '<s>' + priceMoney(compareAt) + '</s> ' + priceMoney(price)
            : priceMoney(price);
          priceEl.style.display = 'block';
        }
        // write into Design summary so order records selections
        var sel = currentCatSelection();
        if (sel && studio && studio.state) {
          studio.state.design = studio.state.design || {};
          studio.state.design.productType = sel.categoryName;
          studio.state.design.size = sel.size ? sel.size.label : '';
          studio.state.design.material = sel.material ? sel.material.label : '';
          studio.state.design.process = sel.process ? sel.process.label : '';
        }
      }

      select.addEventListener('change', applyCategory);
      applyCategory();

      // ---- Photo / AI dual mode ----
      var modeTabs = qsa(root, '[data-studio-mode]');
      var aiGenerateBtn = qs(root, '[data-studio-ai-generate]');
      var aiPrompt = qs(root, '[data-studio-ai-prompt]');
      var aiStatus = qs(root, '[data-studio-ai-status]');
      var aiResults = qs(root, '[data-studio-ai-results]');

      modeTabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var mode = tab.getAttribute('data-studio-mode');
          modeTabs.forEach(function (t) {
            var on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          var up = qs(root, '[data-studio-mode-panel="upload"]');
          var ai = qs(root, '[data-studio-mode-panel="ai"]');
          if (up) up.hidden = mode !== 'upload';
          if (ai) ai.hidden = mode !== 'ai';
        });
      });

      if (aiGenerateBtn && aiPrompt) {
        aiGenerateBtn.addEventListener('click', function () {
          var prompt = (aiPrompt.value || '').trim();
          if (!prompt) return;
          var provider = global.AIProvider;
          if (aiStatus) { aiStatus.hidden = false; aiStatus.textContent = 'Generating…'; }
          var p = (provider && provider.generate) ? provider.generate(prompt, {}) : Promise.reject(new Error('AI not ready'));
          p.then(function (res) {
            var imgs = (res && res.images) || [];
            if (aiStatus) { aiStatus.hidden = true; }
            if (aiResults) {
              aiResults.innerHTML = '';
              imgs.forEach(function (src) {
                var img = document.createElement('img');
                img.src = src; img.alt = prompt; img.className = 'studio__ai-result';
                img.addEventListener('click', function () {
                  // Use AI image as the design source: set as current image then go to step 3 (personalize)
                  if (studio && studio.loadImageDataURL) studio.loadImageDataURL(src);
                  if (studio && studio.setStep) studio.setStep(3);
                });
                aiResults.appendChild(img);
              });
            }
          }).catch(function (err) {
            if (aiStatus) { aiStatus.textContent = 'AI unavailable: ' + (err && err.message || ''); }
          });
        });
      }
    });
  }

  /* ---------- Boot ---------- */

  function init() {
    var roots = qsa(document, '[data-design-studio]');
    roots.forEach(function (root) {
      if (root.__studio) return;
      root.__studio = new Studio(root);
    });
    initCategoryFusion();
  }

  global.LagomloStudio = { init: init, Studio: Studio };
})(window);
